import {
  getAuthAudienceForOrigin,
  getAuthCookieNames,
  HttpHeaderNames,
  isSecureCookieBrowserContext,
} from '@repo/common';

import { getCookie } from '../utils/cookie';

/**
 * Options for fetch requests with CSRF protection
 */
export interface FetchWithCsrfOptions extends Omit<RequestInit, 'credentials'> {
  /** Skip CSRF token (for GET requests or when not needed) */
  skipCsrf?: boolean;
}

/**
 * Base fetch client with automatic CSRF token handling.
 *
 * - Automatically includes CSRF token from cookies
 * - Sets credentials: 'include' for cookie handling
 * - Use this as the foundation for all authenticated API calls
 *
 * @param url - The URL to fetch
 * @param options - Fetch options (same as native fetch, plus skipCsrf)
 * @returns Fetch Response
 *
 * @example
 * ```ts
 * const response = await fetchWithCsrf('/api/endpoint', {
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify(data),
 * });
 * ```
 */
export async function fetchWithCsrf(url: string, options: FetchWithCsrfOptions = {}): Promise<Response> {
  const { skipCsrf = false, headers: customHeaders, ...restOptions } = options;

  const headers: Record<string, string> = {
    ...(customHeaders as Record<string, string>),
  };

  if (!skipCsrf) {
    const audience = getAuthAudienceForOrigin(window.location.origin);
    if (!audience) {
      throw new Error('Unable to resolve auth audience for the current browser origin.');
    }
    const cookieNames = getAuthCookieNames(audience, isSecureCookieBrowserContext());
    const csrfToken = getCookie(cookieNames.csrfToken) ?? '';
    headers[HttpHeaderNames.CsrfToken] = csrfToken;
  }

  return fetch(url, {
    ...restOptions,
    credentials: 'include',
    headers,
  });
}

/**
 * JSON POST request with CSRF protection
 */
export async function postJson<T = unknown>(url: string, data: unknown): Promise<T> {
  const response = await fetchWithCsrf(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
